import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "@/components/ui/dropdown-menu";
import { Layers, ChevronDown, Check, RotateCcw } from "lucide-react";

/**
 * The single "Layers" chip that replaces the six per-kind toggle chips.
 *
 * Layout (top-to-bottom):
 *   1. Header row: "Show layers" + quick "All on" / "All off" links
 *   2. Layer toggle rows — each layer, with its filters (When / Type / Open now)
 *      nested INSIDE the row instead of hanging at the bottom of the menu.
 *   3. Footer: Reset link (returns everything to defaults).
 *
 * The nested-filter pattern keeps the mental model "here's a layer, here are its filters"
 * rather than "here's every filter in one dump".
 */
export default function LayersMenu({
    KIND_META, activeKinds, onToggleKind, chipCount,
    GIG_SUB_LABELS, gigSubKind, onGigSubKind,
    GIG_TIME_LABELS, gigTime, onGigTime,
    openNow, onOpenNow,
}) {
    const layerKeys = Object.keys(KIND_META);
    const activeCount = layerKeys.filter((k) => activeKinds[k]).length;
    const totalCount = layerKeys.length;
    const allOn = activeCount === totalCount;

    const chipLabel = activeCount === 0
        ? "Layers off"
        : allOn ? `All layers · ${totalCount}` : `Layers · ${activeCount}`;

    const setAll = (on) => {
        // Fire one toggle per key that needs to flip so the parent's setter stays simple.
        for (const k of layerKeys) {
            if (!!activeKinds[k] !== on) onToggleKind(k);
        }
    };
    const reset = () => {
        setAll(true);
        onGigSubKind("all");
        onGigTime("all");
        onOpenNow(false);
    };
    const anyFilterActive = gigSubKind !== "all" || gigTime !== "all" || openNow;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    data-testid="layers-menu-toggle"
                    className={`inline-flex flex-shrink-0 items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all hover:-translate-y-0.5 border-l-4 ${
                        activeCount > 0
                            ? "glass shadow-md text-foreground border-l-primary"
                            : "bg-white/50 text-muted-foreground border border-stone-200 border-l-stone-300"
                    }`}
                    title="Choose which layers show on the map"
                >
                    <Layers size={14} className={activeCount > 0 ? "text-primary" : ""} />
                    <span>{chipLabel}</span>
                    <span className="hidden sm:inline-flex items-center gap-0.5 ml-1">
                        {layerKeys.filter((k) => activeKinds[k]).map((k) => (
                            <span key={k} className="w-1.5 h-1.5 rounded-full" style={{ background: KIND_META[k].hex }} />
                        ))}
                    </span>
                    {anyFilterActive && (
                        <span data-testid="layers-menu-filter-dot" className="w-1.5 h-1.5 rounded-full bg-accent" title="Filters applied"/>
                    )}
                    <ChevronDown size={12} className="opacity-60" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-80 p-0" data-testid="layers-menu">
                {/* ----- Header ----- */}
                <div className="flex items-baseline justify-between px-4 pt-3 pb-2">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Show layers</div>
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold">
                        <button
                            data-testid="layers-all-on"
                            onClick={() => setAll(true)}
                            className={`transition-colors ${allOn ? "text-muted-foreground/50 cursor-default" : "text-primary hover:opacity-80"}`}
                            disabled={allOn}
                        >All on</button>
                        <span className="text-muted-foreground/40">·</span>
                        <button
                            data-testid="layers-all-off"
                            onClick={() => setAll(false)}
                            className={`transition-colors ${activeCount === 0 ? "text-muted-foreground/50 cursor-default" : "text-muted-foreground hover:text-foreground"}`}
                            disabled={activeCount === 0}
                        >All off</button>
                    </div>
                </div>

                {/* ----- One global "Open now" switch ----- */}
                <div className="px-4 pb-2">
                    <button type="button" role="switch" aria-checked={openNow} onClick={() => onOpenNow(!openNow)}
                        data-testid="layers-open-now-toggle"
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-sm font-bold transition-colors ${
                            openNow ? "bg-green-600 border-green-600 text-white" : "bg-stone-100 border-stone-200 text-stone-500"}`}>
                        <span className={`relative inline-block w-9 h-5 rounded-full transition-colors ${openNow ? "bg-white/30" : "bg-stone-300"}`}>
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${openNow ? "left-[18px]" : "left-0.5"}`} />
                        </span>
                        {openNow ? "Open now" : "Only show places open now"}
                        {openNow && <Check size={14} className="ml-auto" />}
                    </button>
                </div>

                {/* ----- Layer rows with nested filters ----- */}
                <div className="px-2 pb-2">
                    {layerKeys.map((k) => {
                        const meta = KIND_META[k];
                        const Icon = meta.Icon;
                        const active = !!activeKinds[k];
                        const n = chipCount(k);
                        return (
                            <div key={k}>
                                <button
                                    data-testid={`layers-toggle-${k}`}
                                    onClick={() => onToggleKind(k)}
                                    className={`w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm transition-colors ${
                                        active ? "bg-secondary/70" : "hover:bg-secondary/40"
                                    }`}
                                >
                                    <span
                                        className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                                        style={{ background: `${meta.hex}22` }}
                                    >
                                        <Icon size={15} style={{ color: meta.hex }} />
                                    </span>
                                    <span className={`flex-1 text-left ${active ? "font-semibold" : "text-muted-foreground"}`}>{meta.label}</span>
                                    <span className="text-xs text-muted-foreground tabular-nums">{n}</span>
                                    <span
                                        aria-hidden
                                        className={`w-10 h-5.5 rounded-full flex items-center transition-colors ${
                                            active ? "bg-primary justify-end" : "bg-stone-300 justify-start"
                                        }`}
                                        style={{ width: 40, height: 22 }}
                                    >
                                        <span className="w-4 h-4 rounded-full bg-white shadow mx-0.5" />
                                    </span>
                                </button>

                                {/* Gig filters — nested inline under the Gigs row */}
                                {k === "gig" && active && (
                                    <div className="ml-11 mr-1 mb-2 p-2.5 rounded-lg bg-secondary/40 border border-stone-100" data-testid="layers-gig-filters">
                                        <div className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5">When</div>
                                        <div className="flex flex-wrap gap-1">
                                            {Object.entries(GIG_TIME_LABELS).map(([v, label]) => (
                                                <button
                                                    key={v}
                                                    data-testid={`layers-gig-time-${v}`}
                                                    onClick={() => onGigTime(v)}
                                                    className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-colors ${
                                                        gigTime === v ? "bg-accent text-white" : "bg-white/70 text-muted-foreground hover:text-foreground"
                                                    }`}
                                                >{label}</button>
                                            ))}
                                        </div>
                                        <div className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground mt-2.5 mb-1.5">Type</div>
                                        <div className="flex flex-wrap gap-1">
                                            {Object.entries(GIG_SUB_LABELS).map(([v, label]) => (
                                                <button
                                                    key={v}
                                                    data-testid={`layers-gig-subkind-${v}`}
                                                    onClick={() => onGigSubKind(v)}
                                                    className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-colors ${
                                                        gigSubKind === v ? "bg-primary text-white" : "bg-white/70 text-muted-foreground hover:text-foreground"
                                                    }`}
                                                >{label}</button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                            </div>
                        );
                    })}
                </div>

                {/* ----- Footer ----- */}
                <div className="px-4 pt-2 pb-3 border-t border-stone-100 flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground">Tap a layer to toggle. Filters appear inline.</span>
                    <button
                        data-testid="layers-reset"
                        onClick={reset}
                        className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest text-muted-foreground hover:text-primary"
                    >
                        <RotateCcw size={11} /> Reset
                    </button>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
